import * as React from 'react';
import { Neo, NeoGrid, Views } from '@singularsystems/neo-react';
import { observer } from 'mobx-react';
import { AppService, Types } from '../Services/AppService';
import DashboardLookup from '../../Domain/Models/Dashboard/DashboardLookup';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import UtilisationLookup from '../../Domain/Models/IssuerInvestments/Lookups/UtilisationLookup';
import { Data, Misc, NumberUtils } from '@singularsystems/neo-core';
import DashboardNoteCriteria from '../../Domain/Models/Dashboard/DashboardNoteCriteria';
import DashboardNoteLookup, { LinkType } from '../../Domain/Models/Dashboard/DashboardNoteLookup';
import { NotificationDuration } from '../Models/NotificationDuration';
import IssuerView from '../../Domain/Views/Issuer/IssuerView';
import InvestorView from '../../Domain/Views/Investor/InvestorView';
import IssuerInvestmentView from '../../Domain/Views/IssuerInvestments/IssuerInvestmentView';
import InvestorInvestmentView from '../../Domain/Views/InvestorInvestments/InvestorInvestmentView';
import SystemNoteLookup from '../../Domain/Models/Catalogue/Reporting/Lookups/SystemNoteLookup';
import NoteDetail from '../../Domain/Models/Notes/NoteDetail';

@observer
export default class Home extends Views.ViewBase<HomeVM> {

    constructor(props: unknown) {
        super("", HomeVM, props);
    }

    private iconFactory = AppService.get(Types.Neo.Components.IconFactory);

    public render() {
        const dashboardData = this.viewModel.dashboardData;

        return (
            <>
                <div>
                    {dashboardData && dashboardData.hasNotices &&
                        <Neo.Card title="Notices">
                            {!dashboardData.hasHolidaysForYear &&
                                <Neo.Alert variant="warning" icon="exclamation-triangle">
                                    There are no holidays configured for the current year
                                </Neo.Alert>}
                        </Neo.Card>}

                    <Neo.GridLayout lg={2} xxl={3}>
                        {dashboardData && this.renderDashboardData(dashboardData)}

                        <Neo.Card title="Reminders">
                            {this.viewModel.pageManager.data.length == 0 &&
                                <div>
                                    No upcoming reminders
                                </div>}
                            <Neo.Pager pageManager={this.viewModel.pageManager} pageControls="bottom">
                                <NeoGrid.Grid<DashboardNoteLookup>>
                                    {(item, meta) => (
                                        <NeoGrid.Row className={item.getRowClassName()} showInHeader={false}>
                                            <NeoGrid.ButtonColumn>
                                                <span data-tip={item.linkTypeText}>
                                                    {this.iconFactory.getIconComponent(item.linkTypeIcon, { fixedWidth: true })}
                                                </span>
                                            </NeoGrid.ButtonColumn>
                                            <NeoGrid.Column>
                                                {item.id ? <Neo.Link to={this.getNoteLink(item)} >{item.name}</Neo.Link> : item.name}
                                            </NeoGrid.Column>
                                            <NeoGrid.Column className="dashboard-reminder-column">
                                                <strong>{item.noteType}</strong><br />
                                                {item.noteDescription}
                                            </NeoGrid.Column>
                                            <NeoGrid.Column display={meta.reminderDate} label="Date" />
                                            <NeoGrid.ButtonColumn
                                                editButton={
                                                    item.isSystemNote ?
                                                        {
                                                            icon: "flag-checkered", tooltip: "Confirm & Complete",
                                                            onClick: () => this.viewModel.getSystemNote(item)
                                                        } :
                                                        {
                                                            icon: "flag-checkered", tooltip: "Mark complete",
                                                            onClick: () => this.viewModel.completeNote(item)
                                                        }
                                                } />
                                        </NeoGrid.Row>
                                    )}
                                </NeoGrid.Grid>
                            </Neo.Pager>
                        </Neo.Card>
                    </Neo.GridLayout>
                </div>
                <Neo.Modal
                    title={this.viewModel.systemNote?.header ?? "System Note Details"}
                    size="lg"
                    bindModel={this.viewModel.meta.systemNote}
                    acceptButton={{
                        text: "Confirm and Complete",
                        icon: "save",
                        onClick: () => {
                            this.viewModel.completeSystemNote();
                        }
                    }}>
                    {(systemNote: SystemNoteLookup) => (this.renderSystemNote(systemNote))}
                </Neo.Modal>
            </>
        )
    }

    private getNoteLink(note: DashboardNoteLookup) {
        switch (note.linkType) {
            case LinkType.Issuer: return this.navigation.getPathForView(IssuerView, { issuerId: note.id });
            case LinkType.Investor: return this.navigation.getPathForView(InvestorView, { investorId: note.id });
            case LinkType.IssuerInvestment: return this.navigation.getPathForView(IssuerInvestmentView, { issuerInvestmentId: note.id });
            case LinkType.InvestorInvestment: return this.navigation.getPathForView(InvestorInvestmentView, { investorInvestmentId: note.id });
        }
        return this.navigation.getCurrentViewPath();
    }

    public renderSystemNote(systemNote: SystemNoteLookup) {
        return <div>
            <NeoGrid.Grid<NoteDetail> items={systemNote.details} >
                {(detail, detailMeta) => (
                    <NeoGrid.Row>
                        <NeoGrid.Column display={detailMeta.refNo} />
                        <NeoGrid.Column display={detailMeta.name} />
                        {systemNote.showAmount && <NeoGrid.Column display={detailMeta.amount} />}
                    </NeoGrid.Row>
                )}
            </NeoGrid.Grid>
        </div>;
    }

    private renderDashboardData(data: DashboardLookup) {
        const firstEntity = data.byEntity.find(c => c.utilisation !== null),
            firstUtilisationType = firstEntity?.utilisation[0];

        return (
            <div>
                {firstUtilisationType &&
                    <Neo.Card title="Utilisation">
                        {this.renderUtilisationGraph(firstUtilisationType)}

                        <Neo.GridLayout md={firstUtilisationType.hasLiquidityProviders ? 2 : 1}>
                            <div className="text-center dashboard-value">
                                <strong>Spare Capacity</strong><br />
                                <span>{NumberUtils.format(firstUtilisationType.getSpareCapacity(false), Misc.NumberFormat.CurrencyNoDecimals)}</span>
                            </div>
                            {firstUtilisationType.hasLiquidityProviders &&
                                <div className="text-center dashboard-value">
                                    <strong>Available Liquidity</strong><br />
                                    <span>{NumberUtils.format(firstUtilisationType.remainingLiquidity, Misc.NumberFormat.CurrencyNoDecimals)}</span>
                                </div>}
                        </Neo.GridLayout>
                    </Neo.Card>}
            </div>
        )
    }

    private renderUtilisationGraph(utilisation: UtilisationLookup) {

        const total = utilisation.issuerInvestmentBalance;
        const investorUtilised = utilisation.utilisedAmount - (utilisation.liquidityUtilisation?.sum(c => c.balance) ?? 0);

        const maxLiquidity = utilisation.maxLiquidity;
        const maxLiquidityPercent = utilisation.maxLiquidity / total;
        const availableLiquidity = utilisation.remainingLiquidity;
        const availableLiquidityPercent = availableLiquidity / utilisation.maxLiquidity;

        const capacity = [
            ["Invested", investorUtilised],
            ["Spare Capacity", total - investorUtilised]];

        const liquidity = [
            ["Liquidity<br/>Utilised", maxLiquidity - availableLiquidity],
            ["Liquidity<br/>Available", availableLiquidity]]

        const capacityColors = ["#44959A", "#D5E6E7"];
        const liquidityColors = availableLiquidityPercent > 0.5 ?
            ["#8AC491", "#C0DEC4"] : availableLiquidityPercent > 0.2 ?  // Green
                ["#D5D469", "#E3E2B1"] :                                    // Yellow
                ["#D67A68", "#DFACA5"]                                      // Red

        const mainSeries = {
            data: capacity,
            size: '80%',
            innerSize: '75%',
            colors: capacityColors,
        } as Highcharts.SeriesPieOptions;

        const series = [mainSeries];

        let centerX = "50%";

        if (utilisation.hasLiquidityProviders) {
            series.push({
                data: liquidity,
                size: mainSeries.size,
                innerSize: mainSeries.innerSize,
                colors: liquidityColors,
                startAngle: - (maxLiquidityPercent * 360),
                endAngle: 0,
            } as Highcharts.SeriesPieOptions);

            // Make the main series smaller to fit inside the liqiudity slice.
            mainSeries.size = '58%';
            mainSeries.innerSize = '70%';
            centerX = "55%";
        }

        const chartOptions = {
            chart: {
                type: "pie"
            },
            plotOptions: {
                pie: {
                    tooltip: { valueDecimals: 0, pointFormat: "<b>R{point.y}</b>" },
                    dataLabels: { format: "<b>{point.name}</b>: {point.percentage:.1f} %", style: { fontSize: "10px" } },
                    center: [centerX, "50%"]
                }
            },
            title: { text: "" },
            series: series,
        } as Highcharts.Options;

        return (
            <div>
                <HighchartsReact
                    highcharts={Highcharts}
                    options={chartOptions} />
            </div>
        )
    }
}

class HomeVM extends Views.ViewModelBase {

    constructor(
        taskRunner = AppService.get(Types.Neo.TaskRunner),
        private dashboardApiClient = AppService.get(Types.Domain.ApiClients.DashboardApiClient),
        private notifications = AppService.get(Types.Neo.UI.GlobalNotifications)
    ) {
        super(taskRunner);
        this.makeObservable();
    }

    public dashboardTask = AppService.get(Types.Neo.TaskRunner);

    public dashboardData: DashboardLookup | null = null;

    public systemNote: SystemNoteLookup | null = null;

    public async initialise() {
        const data = await this.dashboardTask.waitForData(this.dashboardApiClient.getDashboardData());
        this.dashboardData = DashboardLookup.fromJSObject<DashboardLookup>(data);
    }

    public noteCriteria = new DashboardNoteCriteria();

    public pageManager = new Data.PageManager(this.noteCriteria, DashboardNoteLookup, this.dashboardApiClient.getReminderNotesPaged, {
        pageSize: 5,
        taskRunner: this.dashboardTask,
        fetchInitial: true,
        allowSort: true,
        sortBy: "reminderDate",
        sortAscending: true
    });

    public async completeNote(note: DashboardNoteLookup) {
        this.taskRunner.run(async () => {
            await this.dashboardApiClient.completeNote(note.noteId);
            await this.pageManager.refreshData();
            this.notifications.addSuccess("Note Completed", "Note completed successfully.", NotificationDuration.Standard);
        });
    }

    public async getSystemNote(note: DashboardNoteLookup) {
        this.taskRunner.run(async () => {
            const response = await this.dashboardApiClient.getSystemNote(note.noteId);
            this.systemNote = SystemNoteLookup.fromJSObject<SystemNoteLookup>(response.data);
        });
    }

    public async completeSystemNote() {
        if (this.systemNote) {
            const noteId = this.systemNote.noteId;
            this.taskRunner.run(async () => {
                if (this.systemNote) {
                    await this.dashboardApiClient.completeSystemNote(noteId);
                    this.systemNote = null;
                    await this.pageManager.refreshData();
                    this.notifications.addSuccess("System Note Completed", "System note completed successfully.", NotificationDuration.Standard);
                }
            });
        }
    }
}